import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { Loading } from '@components/ui/loading/loading';
import { SecondaryButton } from '@components/button/secondary-button/secondary-button';
import { OrchestrationCatalogSearchComponent } from '@app/pages/admin/components/orchestration-catalog-search/orchestration-catalog-search';
import { CopyableIdentifierComponent } from '@app/pages/admin/components/copyable-identifier/copyable-identifier';
import { catalogQueryMatches } from '@app/pages/admin/orchestration-catalog-search.util';
import { CatalogAuthorsComponent } from '@app/pages/admin/developer-catalog-page/catalog-authors/catalog-authors';
import { DeveloperCatalogStore } from '@app/pages/admin/developer-catalog-page/developer-catalog.store';
import {
  CatalogAuthorProfile,
  CatalogCommand,
  CatalogController,
  CatalogDto,
  CatalogJob,
  CatalogMediatorKind,
  CatalogServiceProfile,
  actionCqrsIssuesTooltip,
  buildCatalogAuthorProfiles,
  buildCatalogServiceProfiles,
  catalogGroupLabel,
  catalogKindSeverity,
  commandReferenceCount,
  commandReferenceSources,
  cqrsIssuesTooltip,
  groupByCatalogGroup,
  groupCatalogAuthorParticipations,
  developerCatalogCommandLink,
  developerCatalogControllerFragment,
  developerCatalogControllerLink,
  developerCatalogJobLink,
  externalServicesTooltip,
  formatExecutionSummary,
  formatExecutionTooltip,
  isArchiveGroup,
  isCatalogItemObsolete,
  isCqrsCompliant,
  isExternalServiceNamingCompliant,
  isOrchestrationCompliant,
  isLegacyCronJob,
  isPlacementCompliant,
  jobReferenceCount,
  orchestrationIssuesTooltip,
  externalServiceNamingIssuesTooltip,
  placementIssuesTooltip,
  runtimeSeverity,
} from '@app/pages/admin/developer-catalog-page/developer-catalog.utils';
import { authorChipColors } from '@app/pages/admin/developer-catalog-page/catalog-authors/catalog-authors';
import { toSignal, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  adminHashesEqual,
  parseAdminHash,
  serializeAdminHash,
} from '@app/pages/admin/admin-hash-state';
import { distinctUntilChanged } from 'rxjs';
import { ServerTranslatePipe } from '@app/utils/pipes/server-translate-pipe';

interface DeveloperCatalogGroup<T> {
  group: string;
  items: T[];
}

const CATALOG_TABS = ['Mediator', 'Jobs', 'Controllers', 'Authors', 'Services'] as const;
type CatalogTab = (typeof CATALOG_TABS)[number];

function isCatalogTab(value: string | undefined): value is CatalogTab {
  return !!value && (CATALOG_TABS as readonly string[]).includes(value);
}

@Component({
  selector: 'app-developer-catalog-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    TranslateModule,
    ButtonModule,
    InputTextModule,
    TableModule,
    TabsModule,
    TagModule,
    TooltipModule,
    Loading,
    CatalogAuthorsComponent,
    ServerTranslatePipe,
    SecondaryButton,
    OrchestrationCatalogSearchComponent,
    CopyableIdentifierComponent,
  ],
  templateUrl: './developer-catalog-page.html',
  styleUrl: './developer-catalog-page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DeveloperCatalogPageComponent {
  private readonly store = inject(DeveloperCatalogStore);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private applyingHash = false;

  readonly commandLink = developerCatalogCommandLink;
  readonly jobLink = developerCatalogJobLink;
  readonly controllerLink = developerCatalogControllerLink;
  readonly controllerFragment = developerCatalogControllerFragment;
  readonly runtimeSeverity = runtimeSeverity;
  readonly isArchiveGroup = isArchiveGroup;
  readonly isCatalogItemObsolete = isCatalogItemObsolete;
  readonly catalogGroupLabel = catalogGroupLabel;
  readonly commandReferenceCount = commandReferenceCount;
  readonly jobReferenceCount = jobReferenceCount;
  readonly commandReferenceSources = commandReferenceSources;
  readonly catalogKindSeverity = catalogKindSeverity;
  readonly formatExecutionSummary = formatExecutionSummary;
  readonly formatExecutionTooltip = formatExecutionTooltip;
  readonly externalServicesTooltip = externalServicesTooltip;
  readonly isCqrsCompliant = isCqrsCompliant;
  readonly cqrsIssuesTooltip = cqrsIssuesTooltip;
  readonly isPlacementCompliant = isPlacementCompliant;
  readonly placementIssuesTooltip = placementIssuesTooltip;
  readonly isOrchestrationCompliant = isOrchestrationCompliant;
  readonly isLegacyCronJob = isLegacyCronJob;
  readonly orchestrationIssuesTooltip = orchestrationIssuesTooltip;
  readonly isExternalServiceNamingCompliant = isExternalServiceNamingCompliant;
  readonly externalServiceNamingIssuesTooltip = externalServiceNamingIssuesTooltip;
  readonly actionCqrsIssuesTooltip = actionCqrsIssuesTooltip;
  readonly authorChipColors = authorChipColors;

  commandReferencesTooltip(command: CatalogCommand): string {
    const sources = commandReferenceSources(command);
    return sources.length
      ? sources.map((source) => `${source.kind}: ${source.name}`).join('\n')
      : 'No inbound references';
  }

  jobReferencesTooltip(job: CatalogJob): string {
    const commands = job.referencedCommands || job.usedCommands || [];
    return commands.length ? commands.join('\n') : 'No command references';
  }

  participationLink(itemKind: string, name: string): string[] {
    if (itemKind === 'Job') {
      return this.jobLink(name);
    }
    if (itemKind === 'Controller') {
      return this.controllerLink();
    }
    return this.commandLink(name);
  }

  participationFragment(itemKind: string, name: string, group?: string): string | undefined {
    return itemKind === 'Controller' ? this.controllerFragment(name, group) : undefined;
  }

  loading = toSignal(this.store.loading$, { initialValue: true });
  error = toSignal(this.store.error$, { initialValue: null as string | null });
  catalog = toSignal(this.store.catalog$, { initialValue: null as CatalogDto | null });

  activeTab = signal<CatalogTab>('Mediator');
  headerQuery = signal('');
  commandFilter = signal('');
  jobFilter = signal('');
  authorFilter = signal('');
  serviceFilter = signal('');
  selectedCommandGroup = signal<string | null>(null);
  selectedJobGroup = signal<string | null>(null);
  selectedCommand = signal<CatalogCommand | null>(null);
  selectedJob = signal<CatalogJob | null>(null);
  selectedAuthorName = signal<string | null>(null);
  selectedServiceKey = signal<string | null>(null);
  controllerFilter = signal('');
  selectedControllerGroup = signal<string | null>(null);
  selectedController = signal<CatalogController | null>(null);

  constructor() {
    this.route.fragment
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((fragment) => this.applyHash(fragment));

    this.store.catalog$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((catalog) => {
        if (catalog) {
          this.applyHash(this.route.snapshot.fragment);
        }
      });
  }

  kindCounts = computed(() => {
    const counts: Record<CatalogMediatorKind, number> = {
      Command: 0,
      Query: 0,
      Request: 0,
      CliCommand: 0,
    };
    for (const command of this.catalog()?.commands ?? []) {
      const kind = command.kind as CatalogMediatorKind;
      if (kind in counts) {
        counts[kind] += 1;
      }
    }
    return counts;
  });

  cqrsIssueCount = computed(
    () => (this.catalog()?.commands ?? []).filter((command) => !isCqrsCompliant(command)).length,
  );

  devopsPlacementIssueCount = computed(
    () =>
      (this.catalog()?.commands ?? []).filter((command) => !isPlacementCompliant(command)).length,
  );

  mediatorCount = computed(() => this.catalog()?.commands?.length ?? 0);

  jobLogicIssueCount = computed(
    () => (this.catalog()?.jobs ?? []).filter((job) => !isOrchestrationCompliant(job)).length,
  );

  leftoverJobCount = computed(
    () => (this.catalog()?.jobs ?? []).filter((job) => isLegacyCronJob(job)).length,
  );

  controllerCount = computed(() => this.catalog()?.controllers?.length ?? 0);

  controllerLogicIssueCount = computed(
    () =>
      (this.catalog()?.controllers ?? []).filter(
        (controller) => !isOrchestrationCompliant(controller),
      ).length,
  );

  filteredCommands = computed(() => {
    const headerQuery = this.headerQuery();
    const query = this.commandFilter().trim().toLowerCase();
    const commands = this.catalog()?.commands ?? [];
    if (!headerQuery.trim() && !query) {
      return commands;
    }
    return commands.filter((command) => {
      const haystack = [
        command.name,
        command.kind,
        command.description,
        ...Object.values(command.descriptions ?? {}),
        command.group,
        command.responseType,
        command.sourcePath,
        ...(command.parameters ?? []).map((parameter) => `${parameter.name} ${parameter.type}`),
        ...(command.callers ?? []).map((caller) => `${caller.kind} ${caller.name}`),
        ...(command.usedByJobs ?? []),
        ...(command.chainedCommands ?? []),
        ...(command.referencedByCommands ?? []),
        ...(command.authors ?? []).flatMap((author) => [author.name, author.initials]),
        ...(command.externalServices ?? []).flatMap((service) => [
          service.name,
          service.project,
          service.entryPoint ?? '',
        ]),
        ...(command.cqrsIssues ?? []),
        isCqrsCompliant(command) ? '' : 'cqrs',
        ...(command.placementIssues ?? []),
        isPlacementCompliant(command) ? '' : 'placement devopsutil devops',
        command.kind === 'CliCommand' ? 'clicommand devops' : '',
        isCatalogItemObsolete(command) ? 'obsolete archive' : '',
        String(command.referenceCount ?? ''),
        String(command.executionCount ?? ''),
        command.lastExecutedAt ? String(command.lastExecutedAt) : '',
      ]
        .join(' ')
        .toLowerCase();
      return catalogQueryMatches(headerQuery, haystack) && (!query || haystack.includes(query));
    });
  });

  filteredJobs = computed(() => {
    const headerQuery = this.headerQuery();
    const query = this.jobFilter().trim().toLowerCase();
    const jobs = this.catalog()?.jobs ?? [];
    if (!headerQuery.trim() && !query) {
      return jobs;
    }
    return jobs.filter((job) => {
      const haystack = [
        job.name,
        job.description,
        ...Object.values(job.descriptions ?? {}),
        job.group,
        job.runtimeStatus,
        job.registrationStatus,
        job.featureFlag ?? '',
        job.sourcePath,
        ...(job.usedCommands ?? []),
        ...(job.referencedCommands ?? []),
        ...(job.authors ?? []).flatMap((author) => [author.name, author.initials]),
        ...(job.externalServices ?? []).flatMap((service) => [
          service.name,
          service.project,
          service.entryPoint ?? '',
        ]),
        ...(job.orchestrationIssues ?? []),
        isOrchestrationCompliant(job) ? '' : 'logic',
        isLegacyCronJob(job) ? 'not a process leftover legacycron' : '',
        isCatalogItemObsolete(job) ? 'obsolete archive' : '',
        String(job.referenceCount ?? ''),
        String(job.executionCount ?? ''),
        job.lastExecutedAt ? String(job.lastExecutedAt) : '',
      ]
        .join(' ')
        .toLowerCase();
      return catalogQueryMatches(headerQuery, haystack) && (!query || haystack.includes(query));
    });
  });

  commandGroups = computed(() => this.groupItems(this.filteredCommands()));
  jobGroups = computed(() => this.groupItems(this.filteredJobs()));

  filteredControllers = computed(() => {
    const headerQuery = this.headerQuery();
    const query = this.controllerFilter().trim().toLowerCase();
    const controllers = this.catalog()?.controllers ?? [];
    if (!headerQuery.trim() && !query) {
      return controllers;
    }
    return controllers.filter((controller) => {
      const haystack = [
        controller.name,
        controller.description,
        ...Object.values(controller.descriptions ?? {}),
        controller.group,
        controller.route,
        controller.sourcePath,
        ...(controller.authors ?? []).flatMap((author) => [author.name, author.initials]),
        ...(controller.orchestrationIssues ?? []),
        ...(controller.actions ?? []).flatMap((action) => [
          action.name,
          action.kind,
          ...(action.httpMethods ?? []),
          ...(action.usedCommands ?? []),
          ...(action.orchestrationIssues ?? []),
          ...(action.cqrsIssues ?? []),
        ]),
        isOrchestrationCompliant(controller) ? '' : 'logic',
        String(controller.cqrsIssueCount ?? ''),
        String(controller.orchestrationIssueCount ?? ''),
      ]
        .join(' ')
        .toLowerCase();
      return catalogQueryMatches(headerQuery, haystack) && (!query || haystack.includes(query));
    });
  });

  controllerGroups = computed(() => this.groupItems(this.filteredControllers()));

  authorProfiles = computed(() => buildCatalogAuthorProfiles(this.catalog()));
  serviceProfiles = computed(() => buildCatalogServiceProfiles(this.catalog()));

  unitOfWorkNamingIssueCount = computed(
    () => this.serviceProfiles().filter((profile) => !profile.namingCompliant).length,
  );

  filteredAuthorProfiles = computed(() => {
    const headerQuery = this.headerQuery();
    const query = this.authorFilter().trim().toLowerCase();
    const profiles = this.authorProfiles();
    if (!headerQuery.trim() && !query) {
      return profiles;
    }
    return profiles.filter((profile) => {
      const haystack = [
        profile.name,
        profile.initials,
        ...profile.participations.flatMap((item) => [item.name, item.group, item.itemKind]),
      ]
        .join(' ')
        .toLowerCase();
      return catalogQueryMatches(headerQuery, haystack) && (!query || haystack.includes(query));
    });
  });

  filteredServiceProfiles = computed(() => {
    const headerQuery = this.headerQuery();
    const query = this.serviceFilter().trim().toLowerCase();
    const profiles = this.serviceProfiles();
    if (!headerQuery.trim() && !query) {
      return profiles;
    }
    return profiles.filter((profile) => {
      const haystack = [
        profile.name,
        profile.project,
        profile.description,
        ...Object.values(profile.descriptions ?? {}),
        ...profile.consumers.flatMap((item) => [
          item.name,
          item.group,
          item.itemKind,
          item.entryPoint ?? '',
          item.kind ?? '',
          ...(item.namingIssues ?? []),
          item.namingCompliant ? '' : 'naming unitofwork',
        ]),
        ...(profile.namingIssues ?? []),
        profile.namingCompliant ? '' : 'naming unitofwork',
      ]
        .join(' ')
        .toLowerCase();
      return catalogQueryMatches(headerQuery, haystack) && (!query || haystack.includes(query));
    });
  });

  activeAuthor = computed(() => {
    const profiles = this.filteredAuthorProfiles();
    const selected = this.selectedAuthorName();
    if (!profiles.length) {
      return null as CatalogAuthorProfile | null;
    }
    if (selected && profiles.some((profile) => profile.name === selected)) {
      return profiles.find((profile) => profile.name === selected) ?? null;
    }
    return profiles[0];
  });

  authorParticipationGroups = computed(() =>
    groupCatalogAuthorParticipations(this.activeAuthor()?.participations),
  );

  activeService = computed(() => {
    const profiles = this.filteredServiceProfiles();
    const selected = this.selectedServiceKey();
    if (!profiles.length) {
      return null as CatalogServiceProfile | null;
    }
    if (selected) {
      const match = profiles.find((profile) => this.serviceKey(profile) === selected);
      if (match) {
        return match;
      }
    }
    return profiles[0];
  });

  activeCommandGroup = computed(() => {
    const groups = this.commandGroups();
    const selected = this.selectedCommandGroup();
    if (!groups.length) {
      return null;
    }
    if (selected && groups.some((group) => group.group === selected)) {
      return selected;
    }
    return groups[0].group;
  });

  activeJobGroup = computed(() => {
    const groups = this.jobGroups();
    const selected = this.selectedJobGroup();
    if (!groups.length) {
      return null;
    }
    if (selected && groups.some((group) => group.group === selected)) {
      return selected;
    }
    return groups[0].group;
  });

  activeControllerGroup = computed(() => {
    const groups = this.controllerGroups();
    const selected = this.selectedControllerGroup();
    if (!groups.length) {
      return null;
    }
    if (selected && groups.some((group) => group.group === selected)) {
      return selected;
    }
    return groups[0].group;
  });

  selectedCommandItems = computed(() => {
    const groupName = this.activeCommandGroup();
    if (!groupName) {
      return [];
    }
    return this.commandGroups().find((group) => group.group === groupName)?.items ?? [];
  });

  selectedJobItems = computed(() => {
    const groupName = this.activeJobGroup();
    if (!groupName) {
      return [];
    }
    return this.jobGroups().find((group) => group.group === groupName)?.items ?? [];
  });

  selectedControllerItems = computed(() => {
    const groupName = this.activeControllerGroup();
    if (!groupName) {
      return [];
    }
    return this.controllerGroups().find((group) => group.group === groupName)?.items ?? [];
  });

  onTabChange(value: string | number): void {
    const next = String(value) as CatalogTab;
    if (this.activeTab() === next) {
      return;
    }
    this.activeTab.set(next);
    if (next === 'Jobs') {
      this.selectedJob.set(null);
      this.selectedJobGroup.set(null);
    } else if (next === 'Controllers') {
      this.selectedController.set(null);
      this.selectedControllerGroup.set(null);
      this.controllerFilter.set('');
    } else if (next === 'Authors') {
      this.selectedAuthorName.set(null);
      this.authorFilter.set('');
    } else if (next === 'Services') {
      this.selectedServiceKey.set(null);
      this.serviceFilter.set('');
    } else if (next === 'Mediator') {
      this.selectedCommand.set(null);
      this.selectedCommandGroup.set(null);
      this.commandFilter.set('');
    }
    this.syncHashToUrl();
  }

  reload(): void {
    this.store.reload();
  }

  selectCommandGroup(group: string): void {
    this.selectedCommandGroup.set(group);
    this.selectedCommand.set(null);
    this.syncHashToUrl();
  }

  selectJobGroup(group: string): void {
    this.selectedJobGroup.set(group);
    this.selectedJob.set(null);
    this.syncHashToUrl();
  }

  selectControllerGroup(group: string): void {
    this.selectedControllerGroup.set(group);
    this.selectedController.set(null);
    this.syncHashToUrl();
  }

  selectController(controller: CatalogController): void {
    this.selectedController.set(controller);
    this.syncHashToUrl();
  }

  selectAuthor(profile: CatalogAuthorProfile): void {
    this.selectedAuthorName.set(profile.name);
    this.syncHashToUrl();
  }

  selectService(profile: CatalogServiceProfile): void {
    this.selectedServiceKey.set(this.serviceKey(profile));
    this.syncHashToUrl();
  }

  serviceKey(profile: CatalogServiceProfile): string {
    return `${profile.name}|${profile.project}`;
  }

  authorChipStyle(profile: CatalogAuthorProfile): Record<string, string> {
    const colors = authorChipColors({
      name: profile.name,
      initials: profile.initials,
    });
    return {
      'background-color': colors.bg,
      color: colors.fg,
    };
  }

  selectCommand(command: CatalogCommand | CatalogCommand[] | undefined): void {
    if (!command || Array.isArray(command)) {
      return;
    }
    this.selectedCommand.set(command);
    if (command.group) {
      this.selectedCommandGroup.set(command.group);
    }
    this.syncHashToUrl();
  }

  selectJob(job: CatalogJob): void {
    this.selectedJob.set(job);
    if (job.group) {
      this.selectedJobGroup.set(job.group);
    }
    this.syncHashToUrl();
  }

  private applyHash(fragment: string | null | undefined): void {
    if (this.applyingHash) {
      return;
    }
    this.applyingHash = true;
    try {
      const state = parseAdminHash(fragment);
      const tab: CatalogTab = isCatalogTab(state['tab']) ? state['tab'] : 'Mediator';
      this.activeTab.set(tab);

      this.selectedCommandGroup.set(null);
      this.selectedCommand.set(null);
      this.selectedJobGroup.set(null);
      this.selectedJob.set(null);
      this.selectedControllerGroup.set(null);
      this.selectedController.set(null);
      this.selectedAuthorName.set(null);
      this.selectedServiceKey.set(null);

      const catalog = this.catalog();
      if (tab === 'Mediator') {
        if (state['group']) {
          this.selectedCommandGroup.set(state['group']);
        }
        if (state['command'] && catalog) {
          const command = (catalog.commands ?? []).find((item) => item.name === state['command']);
          if (command) {
            this.selectedCommand.set(command);
            if (command.group) {
              this.selectedCommandGroup.set(command.group);
            }
          }
        }
      } else if (tab === 'Jobs') {
        if (state['group']) {
          this.selectedJobGroup.set(state['group']);
        }
        if (state['job'] && catalog) {
          const job = (catalog.jobs ?? []).find((item) => item.name === state['job']);
          if (job) {
            this.selectedJob.set(job);
            if (job.group) {
              this.selectedJobGroup.set(job.group);
            }
          }
        }
      } else if (tab === 'Controllers') {
        if (state['group']) {
          this.selectedControllerGroup.set(state['group']);
        }
        if (state['controller'] && catalog) {
          const controller = (catalog.controllers ?? []).find(
            (item) => item.name === state['controller'],
          );
          if (controller) {
            this.selectedController.set(controller);
            if (controller.group) {
              this.selectedControllerGroup.set(controller.group);
            }
          }
        }
      } else if (tab === 'Authors') {
        if (state['author']) {
          this.selectedAuthorName.set(state['author']);
        }
      } else if (tab === 'Services') {
        if (state['service']) {
          this.selectedServiceKey.set(state['service']);
        }
      }
    } finally {
      this.applyingHash = false;
    }
  }

  private buildHashState(): Record<string, string> {
    const tab = this.activeTab();
    const state: Record<string, string> = {};
    if (tab !== 'Mediator') {
      state['tab'] = tab;
    }

    if (tab === 'Mediator') {
      const group = this.selectedCommandGroup();
      const command = this.selectedCommand()?.name;
      if (group) {
        state['group'] = group;
      }
      if (command) {
        state['command'] = command;
      }
    } else if (tab === 'Jobs') {
      const group = this.selectedJobGroup();
      const job = this.selectedJob()?.name;
      if (group) {
        state['group'] = group;
      }
      if (job) {
        state['job'] = job;
      }
    } else if (tab === 'Controllers') {
      const group = this.selectedControllerGroup();
      const controller = this.selectedController()?.name;
      if (group) {
        state['group'] = group;
      }
      if (controller) {
        state['controller'] = controller;
      }
    } else if (tab === 'Authors') {
      const author = this.selectedAuthorName();
      if (author) {
        state['author'] = author;
      }
    } else if (tab === 'Services') {
      const service = this.selectedServiceKey();
      if (service) {
        state['service'] = service;
      }
    }

    return state;
  }

  private syncHashToUrl(): void {
    if (this.applyingHash) {
      return;
    }
    const fragment = serializeAdminHash(this.buildHashState()) ?? null;
    if (adminHashesEqual(fragment, this.route.snapshot.fragment)) {
      return;
    }
    const tree = this.router.parseUrl(this.router.url);
    tree.fragment = fragment;
    void this.router.navigateByUrl(tree);
  }

  private groupItems<T extends { group?: string | null }>(items: T[]): DeveloperCatalogGroup<T>[] {
    return groupByCatalogGroup(items);
  }
}
